import { useMutation, useQuery } from '@apollo/client/react';
import { FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import { map } from 'lodash';
import { debounce, map as mapFp } from 'lodash/fp';
import React, { useState, useRef, useContext } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import TimeAutocomplete from '@/components/TimeAutocomplete';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import {
  type Event,
  type Report,
  PermissionEntityEnum,
  PermissionActionEnum,
  UpdateEventDocument,
  GetProjectsDocument,
  GetCompanyEventsDocument,
  EventCategoryEnum,
  EventTypeEnum,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

const normalDebounce = debounce(500);
const mapNode = mapFp('node');

interface EditModalProps {
  minimized?: boolean;
  event: Event;
  handleClose: () => void;
  reportDate?: Report['reportDate'];
  project?: { id: string; title: string; code: string } | null;
  handleMinimize?: (label: string) => void;
}

const EditModal: React.FC<EditModalProps> = ({
  event: baseEvent,
  minimized,
  handleClose,
  handleMinimize = undefined,
  project,
  reportDate,
}) => {
  const { currentUser } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const { Projects, Events } = PermissionEntityEnum;
  const { View, Edit } = PermissionActionEnum;

  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [event, setEvent] = useState({ ...baseEvent });
  const [updateEvent, { loading }] = useMutation(UpdateEventDocument, {
    refetchQueries: ['getProjects'],
  });

  const { data } = useQuery(GetProjectsDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !canPerformAction(View, Projects) || Boolean(project),
    variables: {
      companyId: currentUser?.defaultCompany?.id as string,
    },
  });

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    // !TODO: raise a proper error or warning
    if (!canPerformAction(Edit, Events)) return;

    const { id, category, eventType, description, flag, title, occurredAt } = event;

    updateEvent({
      variables: {
        id,
        category,
        eventType,
        description,
        flag,
        ...(title && { title }),
        ...(occurredAt && { occurredAt }),
      },
      onCompleted: () => {
        displayNotification({
          type: 'success',
          text: 'Event updated successfully!',
        });
        handleClose();
      },
      onError: (err) => {
        displayNotification({
          type: 'error',
          text: `Unable to update an event: ${err.message}`,
        });
      },
      update(cache) {
        const companyId = currentUser?.defaultCompany?.id;
        const projectId = event.project?.id || baseEvent.project.id;

        if (!companyId || !projectId) return;
        if (event.occurredAt && dayjs(event.occurredAt).format('HH:mm') === '00:00') return;

        const variables = {
          companyId,
          projectId,
        };
        const existingEvents =
          cache.readQuery({
            query: GetCompanyEventsDocument,
            variables,
          })?.getCompanyEvents ?? [];

        cache.writeQuery({
          query: GetCompanyEventsDocument,
          variables,
          data: {
            getCompanyEvents: map(existingEvents, (existingEvent) =>
              existingEvent.id === event.id
                ? {
                    ...existingEvent,
                    category: event.category,
                    eventType: event.eventType,
                    description: event.description,
                    flag: event.flag,
                    title: event.title,
                    occurredAt: event.occurredAt,
                  }
                : existingEvent,
            ),
          },
        });
      },
    });
  };

  const handleOnChange = (key: string, value: string | EventCategoryEnum | EventTypeEnum, debounce: boolean = true) => {
    if (debounce) {
      debounceFunc.cancel();
      debounceFunc(() => {
        setEvent((prev) => ({ ...prev, [key]: value }));
      });

      return;
    }

    setEvent((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <OverwatchModal
      size="md"
      handleClose={() => {
        handleClose();
        setEvent(baseEvent);
      }}
      title={`Edit Event Form${project ? ` for Project: ${project.code} | ${project.title}` : ''}`}
      caption="Fill out the details below to update the event"
      primaryButtonAction={handleSubmit}
      primaryButtonText={loading ? 'Updating...' : 'Update'}
      disablePrimary={!event.description || loading}
      secondaryButtonAction={handleClose}
      secondaryButtonText="Cancel"
      {...(minimized && { minimized })}
      {...(handleMinimize && { handleMinimize: () => handleMinimize(event.title || 'New Event') })}
    >
      <Grid container noValidate spacing={2} component="form" onSubmit={handleSubmit}>
        {!project && (
          <Grid size={12}>
            <Autocomplete
              options={mapNode(data?.getProjects?.edges || [])}
              getOptionLabel={(option) => `${option.code} | ${option.title}`}
              onChange={(_, value) => handleOnChange('projectId', value?.id || '', false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Project"
                  error={!event.projectId}
                  helperText={!event.projectId ? 'Project is required' : ''}
                />
              )}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            value={event.category || null}
            options={Object.values(EventCategoryEnum)}
            getOptionLabel={(option) => titleize(option)}
            renderInput={(params) => <TextField {...params} label="Category (Optional)" fullWidth />}
            onChange={(_, value) => handleOnChange('category', value || '', false)}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            options={Object.values(EventTypeEnum)}
            getOptionLabel={(option) => titleize(option)}
            value={event.eventType}
            onChange={(_, value) => handleOnChange('eventType', value || '', false)}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Event Type" fullWidth />}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Title"
            defaultValue={event.title}
            onChange={(e) => {
              handleOnChange('title', e.target.value, false);
            }}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            defaultValue={event.description}
            onChange={(e) => {
              handleOnChange('description', e.target.value, false);
            }}
            error={!event.description}
            helperText={!event.description ? 'Description is required' : ''}
          />
        </Grid>
        <Grid size={12}>
          <TimeAutocomplete
            label="Occurred At (optional)"
            time={event.occurredAt ? dayjs(event.occurredAt).format('HH:mm') : '00:00'}
            onTimeChange={(newVal) => {
              setEvent((prev) => ({
                ...prev,
                occurredAt:
                  newVal && reportDate
                    ? dayjs(reportDate)
                        .hour(+(newVal.split(':')[0] ?? 0))
                        .minute(+(newVal.split(':')[1] ?? 0))
                        .second(0)
                        .millisecond(0)
                        .format()
                    : null,
              }));
            }}
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            sx={{ alignSelf: 'flex-start' }}
            control={
              <Switch
                onChange={(_, checked) => setEvent((prev) => ({ ...prev, flag: checked }))}
                defaultChecked={event.flag}
              />
            }
            label="Flag this event"
          />
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default EditModal;
